import { useEffect, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import { Navigate } from 'react-router-dom';

import { labRoutesEnabled } from '@/config/features';
import { getConsoleLandingPath } from '@/navigation/consoleNavigation';
import { useAuthStore } from '@/stores/authStore';

interface LabRouteGuardProps {
  children: ReactNode;
  requireOrganizationScope?: boolean;
}

export default function LabRouteGuard({
  children,
  requireOrganizationScope = true,
}: LabRouteGuardProps) {
  const userRole = useAuthStore((state) => state.user?.role ?? null);
  const isOwner = useAuthStore((state) => state.isOwner);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const isOrganizationScope = !isOwner || isImpersonating;
  const landingPath = getConsoleLandingPath({
    userRole,
    isOwner,
    isOrganizationScope,
    enableLabRoutes: labRoutesEnabled,
  });

  const blocked =
    !labRoutesEnabled ||
    userRole === 'DEVICE' ||
    (requireOrganizationScope && !isOrganizationScope);

  useEffect(() => {
    if (blocked && !labRoutesEnabled) {
      // Say why the page changed instead of landing silently elsewhere.
      toast('That page is an engineering tool and isn’t available in this console.', {
        id: 'lab-route-redirect',
      });
    }
  }, [blocked]);

  if (blocked) {
    return <Navigate to={landingPath} replace />;
  }

  return <>{children}</>;
}
